// ─── Tag Categories & Tags ─────────────────────────────────────────────────

import type {
  Tag,
  TagCategory,
  TagValue,
  TagValueType,
  TagValueValidation,
} from '../../graphql/types';
import { apiRequest } from './http';
import { createEntityCrudClient } from './crudFactory';

export interface CreateTagCategoryInput {
  name: string;
  description?: string;
  scope: 'global' | 'team';
  scopeOwnerId?: string;
  color?: string;
  icon?: string;
  allowMultiple?: boolean;
  applicableEntityTypes?: string[];
  valueType?: TagValueType;
  validation?: TagValueValidation;
  displayOrder?: number;
}

export interface UpdateTagCategoryInput {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  allowMultiple?: boolean;
  applicableEntityTypes?: string[];
  validation?: TagValueValidation;
  status?: string;
  displayOrder?: number;
}

const categoryClient = createEntityCrudClient<
  TagCategory,
  CreateTagCategoryInput,
  UpdateTagCategoryInput
>('/tag-categories');

export async function createTagCategory(input: CreateTagCategoryInput): Promise<TagCategory> {
  return categoryClient.create(input);
}

export async function updateTagCategory(
  categoryId: string,
  input: UpdateTagCategoryInput
): Promise<TagCategory> {
  return categoryClient.update(categoryId, input);
}

export async function deleteTagCategory(
  categoryId: string,
  hardDelete: boolean = false
): Promise<void> {
  return categoryClient.remove(categoryId, hardDelete);
}

export interface CreateTagInput {
  name: string;
  categoryId: string;
  color?: string;
  icon?: string;
  description?: string;
  value?: TagValue;
  displayOrder?: number;
}

export interface UpdateTagInput {
  name?: string;
  color?: string;
  icon?: string;
  description?: string;
  value?: TagValue;
  status?: string;
  displayOrder?: number;
}

const tagClient = createEntityCrudClient<Tag, CreateTagInput, UpdateTagInput>('/tags');

export async function createTag(input: CreateTagInput): Promise<Tag> {
  return tagClient.create(input);
}

export async function updateTag(tagId: string, input: UpdateTagInput): Promise<Tag> {
  return tagClient.update(tagId, input);
}

export async function deleteTag(tagId: string, hardDelete: boolean = false): Promise<void> {
  return tagClient.remove(tagId, hardDelete);
}

export async function addTagToEntity(
  entityType: string,
  entityId: string,
  tagId: string
): Promise<void> {
  return apiRequest<void>(`/entities/${entityType}/${entityId}/tags`, {
    method: 'POST',
    body: JSON.stringify({ tagId }),
  });
}

export async function removeTagFromEntity(
  entityType: string,
  entityId: string,
  tagId: string
): Promise<void> {
  return apiRequest<void>(`/entities/${entityType}/${entityId}/tags/${tagId}`, {
    method: 'DELETE',
  });
}
